import React, { useContext } from 'react';
import DDMAttributesProvider from 'src/integration/ddm/providers/DDMAttributesProvider';
import { useGetSchemaQuery } from 'src/state/slice/ddmServiceApi';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { KatAlert, KatSpinner } from '@amzn/katal-react';

import { logger } from 'src/logger';
import isEmpty from 'lodash/isEmpty';
import {
  DDM_CPR_CLIENT_ID,
  PAGE_SCOPE,
  UI_CLIENTS,
} from 'src/constants/GlobalConstants';
import { DDMSchemaParser } from 'src/integration/ddm/DDMSchemaParser';
import { TranslationContext } from 'src/state/providers/TranslationProvider';
import { PageContext } from 'src/state/providers/PageProvider';

interface IDDMContextInjector {
  schemaKey: string;
  clientId?: string;
}

const DDMContextInjector: React.FC<IDDMContextInjector> = ({
  schemaKey,
  clientId = DDM_CPR_CLIENT_ID,
  children,
}) => {
  const authContext = useContext(AuthContext);
  const pageContext = useContext(PageContext);
  const bundles = useContext(TranslationContext);

  const { isLoading, data, isError, error } = useGetSchemaQuery({
    schemaKey: schemaKey,
    clientId: clientId,
    isPublicPage: PAGE_SCOPE.PUBLIC === pageContext?.pageScope,
    isRegulatorCentralApp: authContext.clientId === UI_CLIENTS.RegulatorCentral,
  });

  if (isLoading) {
    return <KatSpinner data-testid="ddmDataFetchLoader" />;
  }

  if (isError) {
    logger.error(
      `Unable to fetch schema for user: ${
        authContext.customerId
      } with accountId: ${schemaKey} Check details in context:   ${JSON.stringify(
        error,
      )}`,
    );
    return (
      <KatAlert
        data-testid="ddmFetchErrorMesage"
        variant="danger"
        title="Error"
        description="Unable to Fetch Schema from config. Kindly try again."
        persistent
      />
    );
  }

  if (isEmpty(data) || isEmpty(data.schemaDefinition)) {
    logger.error(
      `Invalid schema for user: ${authContext.customerId} with accountId: ${authContext.preferredAccountId} Check details in context:`,
      data,
    );
    return (
      <KatAlert
        variant="danger"
        title="Error"
        description="No Schema defined for the account."
        persistent
      />
    );
  }

  const ddmSchema = new DDMSchemaParser(data, bundles);
  return (
    <DDMAttributesProvider
      createCaseSchema={ddmSchema.getCreateCaseSchema()}
      formData={ddmSchema.getDefaultFormData()}
      createCaseUiSchema={ddmSchema.getCreateCaseUiSchema()}
      createCaseStaticSchema={ddmSchema.getCreateCaseStaticSchema()}
      caseSummarySchema={ddmSchema.getCaseSummarySchema()}
      caseSummaryUiSchema={ddmSchema.getCaseSummaryUiSchema()}
      caseSummaryFormData={ddmSchema.getCaseSummaryFormData()}
      ddmSchemaId={ddmSchema.getSchemaId()}
      ddmSchemaVersion={ddmSchema.getSchemaVersion()}
    >
      {children}
    </DDMAttributesProvider>
  );
};

export { DDMContextInjector };
